import React, { useEffect, useState } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./ui/dialog";
import { HandCoins, CheckCircle, XCircle, Info, FileArrowDown, Plus, Eye, PencilSimple, FilePdf, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import { money } from "../lib/format";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  needs_info: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-700",
};
const FILTERS = [
  { k: "", label: "Todas" },
  { k: "pending", label: "Pendientes" },
  { k: "approved", label: "Aprobadas" },
  { k: "needs_info", label: "Requiere info" },
  { k: "rejected", label: "Rechazadas" },
];

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-center shadow-sm">
      <div className="font-mono-num text-lg font-bold text-emerald-900 truncate">{value}</div>
      <div className="text-[11px] text-slate-400">{label}</div>
    </div>
  );
}

function DecisionDialog({ app, status, label, icon: Icon, cls, onDone }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/loan-applications/${app.id}/decision`, { status, message });
      toast.success("Decisión guardada y notificada al socio");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`app-${status}-${app.id}`} size="sm" variant="outline" className={`gap-1.5 ${cls}`}>
          <Icon size={15} weight="bold" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle>{label} · {app.application_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Mensaje para el socio (opcional)</Label>
          <Textarea
            data-testid="decision-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={status === "needs_info" ? "Indica qué información o documentos faltan..." : "Escribe un mensaje para el socio..."}
          />
        </div>
        <DialogFooter>
          <Button data-testid="decision-submit" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">
            {busy ? "Guardando..." : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateLoanDialog({ app, onDone }) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (rate === "" || Number(rate) < 0) { toast.error("Indica la tasa anual (%)"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/loan-applications/${app.id}/create-loan`, { annual_rate: Number(rate), address });
      toast.success("Préstamo creado y vinculado al socio");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`create-loan-${app.id}`} size="sm" className="gap-1.5 bg-emerald-800 hover:bg-emerald-900">
          <Plus size={15} weight="bold" /> Crear préstamo
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="create-loan-dialog">
        <DialogHeader>
          <DialogTitle>Crear préstamo · {app.application_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><p className="text-slate-400 text-xs">Monto</p><p className="font-mono-num font-bold text-emerald-900">{money(app.amount, app.currency)}</p></div>
            <div><p className="text-slate-400 text-xs">Plazo</p><p className="font-semibold text-emerald-900">{app.term_months} meses</p></div>
            <div><p className="text-slate-400 text-xs">Moneda</p><p className="font-semibold text-emerald-900">{app.currency === "USD" ? "US$" : "RD$"}</p></div>
          </div>
          <div>
            <Label>Tasa anual (%) *</Label>
            <Input data-testid="create-loan-rate" type="number" min="0" step="0.01" className="mt-1.5 h-11" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Ej: 18" />
          </div>
          <div>
            <Label>Dirección del titular (opcional)</Label>
            <Input data-testid="create-loan-address" className="mt-1.5 h-11" value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="create-loan-submit" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">
            {busy ? "Creando..." : "Crear préstamo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LoanApplicationsPanel({ onChanged }) {
  const { t } = useI18n();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});
  const toggleReveal = (id) => setRevealed((r) => ({ ...r, [id]: !r[id] }));
  const [recips, setRecips] = useState("");
  const [recipsSaved, setRecipsSaved] = useState("");
  const [stats, setStats] = useState(null);
  const [branch, setBranch] = useState("");
  useEffect(() => {
    api.get("/admin/loan-summary-recipients").then((r) => { const v = (r.data.recipients || []).join(", "); setRecips(v); setRecipsSaved(v); }).catch(() => {});
  }, []);
  useEffect(() => {
    const bq = branch ? `&branch=${encodeURIComponent(branch)}` : "";
    api.get(`/admin/loan-applications/stats?period=month${bq}`).then((r) => setStats(r.data)).catch(() => {});
  }, [branch]);
  const saveRecips = async () => {
    const list = recips.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      const r = await api.put("/admin/loan-summary-recipients", { recipients: list });
      const v = (r.data.recipients || []).join(", ");
      setRecips(v); setRecipsSaved(v);
      toast.success("Destinatarios del resumen guardados");
    } catch (e) { toast.error("No se pudo guardar"); }
  };

  const exportStats = async (fmt) => {
    try {
      const bq = branch ? `&branch=${encodeURIComponent(branch)}` : "";
      const res = await api.get(`/admin/loan-applications/stats/${fmt}?period=month${bq}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tablero_solicitudes${branch ? "_" + branch.replace(/\s+/g, "_") : ""}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo exportar el tablero"); }
  };

  const load = () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    if (branch) params.set("branch", branch);
    const qs = params.toString();
    api.get(`/admin/loan-applications${qs ? "?" + qs : ""}`).then((r) => setApps(r.data)).catch(() => {}).finally(() => setLoading(false));
    onChanged && onChanged();
  };
  useEffect(() => { load(); }, [filter, branch]); // eslint-disable-line

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="loan-applications-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <HandCoins size={22} weight="duotone" className="text-emerald-700" />
          <h3 className="font-display font-bold text-emerald-900 text-xl">{t("loan_applications_tab")}</h3>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              data-testid={`app-filter-${f.k || "all"}`}
              onClick={() => setFilter(f.k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${filter === f.k ? "bg-emerald-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {stats && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="loan-dashboard-toolbar">
          {stats.all_branches && stats.all_branches.length > 0 && (
            <>
              <label className="text-xs font-semibold text-slate-600">Sucursal:</label>
              <select
                data-testid="loan-branch-select"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-600"
              >
                <option value="">Todas las sucursales</option>
                {stats.all_branches.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              {branch && (
                <button
                  data-testid="loan-branch-clear"
                  onClick={() => setBranch("")}
                  className="text-xs font-semibold text-emerald-700 hover:underline"
                >
                  Quitar filtro
                </button>
              )}
            </>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" data-testid="loan-stats-export-pdf" onClick={() => exportStats("pdf")} className="inline-flex items-center gap-1 text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-3 h-9 rounded-lg"><FilePdf size={16} weight="bold" /> PDF</button>
            <button type="button" data-testid="loan-stats-export-excel" onClick={() => exportStats("excel")} className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 h-9 rounded-lg"><FileXls size={16} weight="bold" /> Excel</button>
          </div>
        </div>
      )}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="loan-stats">
          <Stat label="Recibidas (30d)" value={stats.received} />
          <Stat label="Aprobadas" value={stats.approved} />
          <Stat label="Tasa aprob." value={`${stats.approval_rate}%`} />
          <Stat label="Solicitado" value={money(stats.total_requested, "DOP")} />
          <Stat label="Aprobado" value={money(stats.total_approved, "DOP")} />
        </div>
      )}
      {stats && stats.by_branch && stats.by_branch.length > 0 && (
        <div className="mb-4 rounded-lg border border-gray-100 overflow-x-auto" data-testid="loan-stats-branch">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Sucursal</th>
                <th className="px-3 py-2 text-right">Recibidas</th>
                <th className="px-3 py-2 text-right">Solicitado</th>
                <th className="px-3 py-2 text-right">Aprobadas</th>
                <th className="px-3 py-2 text-right">Aprobado</th>
              </tr>
            </thead>
            <tbody>
              {stats.by_branch.map((b, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-1.5 font-medium text-emerald-900">{b.branch}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{b.received}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{money(b.requested, "DOP")}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{b.approved}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num text-emerald-700">{money(b.approved_amount, "DOP")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mb-4 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3" data-testid="summary-recipients-box">
        <label className="text-xs font-semibold text-emerald-800 block mb-1">Destinatarios del resumen semanal (correos separados por coma)</label>
        <div className="flex gap-2">
          <Input data-testid="summary-recipients-input" value={recips} onChange={(e) => setRecips(e.target.value)} placeholder="directiva@coopestrella.com, ..." className="h-9" />
          <Button data-testid="summary-recipients-save" onClick={saveRecips} size="sm" disabled={recips === recipsSaved} className="bg-emerald-800 hover:bg-emerald-900">Guardar</Button>
        </div>
      </div>
      {loading ? (
        <p className="text-slate-500 py-6">{t("loading")}</p>
      ) : apps.length === 0 ? (
        <p className="text-slate-400 py-10 text-center border border-dashed border-gray-200 rounded-lg">No hay solicitudes en esta vista.</p>
      ) : (
        <div className="space-y-4">
          {apps.map((a) => (
            <div key={a.id} className="border border-gray-100 rounded-xl p-4 shadow-sm" data-testid={`loan-app-${a.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-num text-sm text-emerald-700 font-semibold">{a.application_number}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[a.status] || "bg-slate-100"}`} data-testid={`app-status-${a.id}`}>{a.status_label}</span>
                  </div>
                  <p className="font-semibold text-emerald-900 mt-1">{a.applicant_name}</p>
                  <p className="text-xs text-slate-400">{a.applicant_email || a.applicant_phone || "—"}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono-num text-2xl font-bold text-emerald-900">{money(a.amount, a.currency)}</p>
                  <p className="text-xs text-slate-500">{a.term_months} meses · {a.payment_frequency}</p>
                </div>
              </div>

              <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-600">
                <div><span className="text-slate-400">Propósito:</span> {a.purpose}</div>
                {a.monthly_income > 0 && <div><span className="text-slate-400">Ingreso mensual:</span> {money(a.monthly_income, a.currency)}</div>}
                {a.occupation && <div><span className="text-slate-400">Ocupación:</span> {a.occupation}</div>}
                {a.employer && <div><span className="text-slate-400">Lugar de trabajo:</span> {a.employer}</div>}
                {a.reference_name && <div><span className="text-slate-400">Referencia:</span> {a.reference_name} {a.reference_phone && `· ${a.reference_phone}`}</div>}
                {a.guarantor_name && <div><span className="text-slate-400">Garante:</span> {a.guarantor_name}</div>}
                {a.comment && <div className="sm:col-span-2"><span className="text-slate-400">Comentario:</span> {a.comment}</div>}
              </div>

              {a.documents && a.documents.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`app-docs-${a.id}`}>
                  {a.documents.map((d) => {
                    const isImg = (d.content_type || "").startsWith("image/");
                    return (
                      <a key={d.id} href={`${BACKEND}${d.url}`} target="_blank" rel="noreferrer" data-testid={`app-doc-${d.id}`} className="group inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-800 px-2.5 py-1.5 rounded-lg border border-emerald-100 hover:bg-emerald-100">
                        {isImg ? (
                          <img src={`${BACKEND}${d.url}`} alt={d.label} className="h-9 w-9 rounded object-cover border border-emerald-200" />
                        ) : (
                          <FileArrowDown size={16} weight="bold" />
                        )}
                        <span className="max-w-[160px] truncate">{d.label}</span>
                        <Eye size={14} weight="bold" className="opacity-60 group-hover:opacity-100" />
                      </a>
                    );
                  })}
                </div>
              )}

              {a.decision_message && (
                <div className="mt-3 text-sm bg-slate-50 rounded-lg px-3 py-2 text-slate-600"><span className="font-semibold">Mensaje enviado:</span> {a.decision_message}</div>
              )}
              {a.decided_by && (
                <p className="mt-2 text-[11px] text-slate-400">Revisado por {a.decided_by}{a.decided_at ? ` · ${(a.decided_at || "").slice(0, 10)}` : ""}</p>
              )}

              <div className="mt-3 flex gap-2 flex-wrap items-center">
                {a.loan_created ? (
                  <span data-testid={`app-loan-created-${a.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full">
                    <CheckCircle size={14} weight="bold" /> Préstamo creado: {a.loan_number}
                  </span>
                ) : (a.status === "approved" || a.status === "rejected") && !revealed[a.id] ? (
                  <>
                    <Button data-testid={`change-decision-${a.id}`} size="sm" variant="outline" onClick={() => toggleReveal(a.id)} className="gap-1.5 border-slate-300 text-slate-600 hover:bg-slate-50">
                      <PencilSimple size={15} weight="bold" /> Cambiar decisión
                    </Button>
                    {a.status === "approved" && <CreateLoanDialog app={a} onDone={load} />}
                  </>
                ) : (
                  <>
                    <DecisionDialog app={a} status="approved" label="Aprobar" icon={CheckCircle} cls="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onDone={load} />
                    <DecisionDialog app={a} status="needs_info" label="Pedir más información" icon={Info} cls="border-blue-300 text-blue-700 hover:bg-blue-50" onDone={load} />
                    <DecisionDialog app={a} status="rejected" label="Rechazar" icon={XCircle} cls="border-red-300 text-red-700 hover:bg-red-50" onDone={load} />
                    {a.status === "approved" && <CreateLoanDialog app={a} onDone={load} />}
                    {(a.status === "approved" || a.status === "rejected") && (
                      <Button data-testid={`cancel-change-${a.id}`} size="sm" variant="ghost" onClick={() => toggleReveal(a.id)} className="text-slate-500">Cancelar</Button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
